import { Intent } from "@blueprintjs/core";
import {
    LOW_CONFIDENCE_LOCATION_CALLOUT_BODY,
    LOW_CONFIDENCE_LOCATION_CALLOUT_TITLE,
    RTOE_CALLOUT_BODY,
    RTOE_CALLOUT_TITLE
} from "components/detailPane/shared/busCalloutMessages";
import DetailPaneCallout from "components/detailPane/shared/DetailPaneCallout";
import getEnvVariables from "config/envVariables";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import {
    Bus,
    isLowConfidenceLocationBus,
    isOutOfRegionBus
} from "types/busType";
import { RegionType } from "types/regionType";
import { formatStudyBusUtilities } from "utils/speculativeProjectUtils";
import "./BusGeneralInfoSection.scss";

const SATELLITE_THUMBNAIL_ZOOM = 14;
const GOOGLE_MAPS_ZOOM = 18;
const SATELLITE_SIZE = 180;

const getGoogleMapsUrl = (lat: number, lng: number): string =>
    `https://www.google.com/maps/@${lat},${lng},${GOOGLE_MAPS_ZOOM}z/data=!3m1!1e3`;

const getSatelliteImageUrl = (
    lat: number,
    lng: number,
    token: string
): string =>
    `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static/${lng},${lat},${SATELLITE_THUMBNAIL_ZOOM}/${SATELLITE_SIZE}x${SATELLITE_SIZE}@2x?access_token=${token}`;

const BusGeneralInfoSection: React.FC = () => {
    const { bus } = useBusPageConfiguration();

    return (
        <>
            <div className="BusGeneralInfoSection-wrapper">
                <SatelliteThumbnail
                    latitude={bus.latitude}
                    longitude={bus.longitude}
                />
                <div className="BusGeneralInfoSection-text">
                    <h1 className="bp5-heading">{bus.busDisplayName}</h1>
                    <BusMetadata bus={bus} />
                </div>
            </div>
            <BusCallouts bus={bus} />
        </>
    );
};

// The same per-bus warnings the map's bus detail pane shows
// (BusDetailPane), so a user who lands here from a link still sees them.
// Trigger conditions are duplicated from BusDetailPane; the copy is
// shared via busCalloutMessages. TODO: consolidate condition + copy +
// intent into shared callout definitions both surfaces render from, so
// future messages show here automatically (deferred to keep the launch
// diff away from the map pane).
const BusCallouts: React.FC<{ readonly bus: Bus }> = ({ bus }) => {
    const showRtoeCallout =
        bus.region === RegionType.WECC && isOutOfRegionBus(bus);
    const showLowConfidenceCallout = isLowConfidenceLocationBus(bus);

    if (!showRtoeCallout && !showLowConfidenceCallout) {
        return null;
    }

    return (
        <div className="BusGeneralInfoSection-callouts">
            {showRtoeCallout && (
                <DetailPaneCallout
                    intent={Intent.DANGER}
                    title={RTOE_CALLOUT_TITLE}
                >
                    {RTOE_CALLOUT_BODY}
                </DetailPaneCallout>
            )}
            {showLowConfidenceCallout && (
                <DetailPaneCallout
                    intent={Intent.WARNING}
                    title={LOW_CONFIDENCE_LOCATION_CALLOUT_TITLE}
                >
                    {LOW_CONFIDENCE_LOCATION_CALLOUT_BODY}
                </DetailPaneCallout>
            )}
        </div>
    );
};

const BusMetadata: React.FC<{ readonly bus: Bus }> = ({ bus }) => {
    // The operating utility, which is not the same thing as the Owner row:
    // `owner_name` is a Velocity Suite company name that merges PACE
    // and PACW into one "PacifiCorp", while the studies — and the New projects
    // step's POI filter — key on the utility. Absent outside WECC, and for a
    // bus the pipeline could not resolve one for, so the row is dropped rather
    // than shown empty. Two, read as alternatives, when the study's two cases
    // resolve the bus differently.
    const utilities = formatStudyBusUtilities(bus);

    return (
        <div className="BusGeneralInfoSection-metadata">
            <MetadataRow label="Bus ID" value={bus.id} />
            <MetadataRow label="Region" value={bus.region} />
            <MetadataRow label="Voltage" value={`${bus.voltage} kV`} />
            <MetadataRow label="Owner" value={bus.ownerName} />
            {utilities !== undefined && (
                <MetadataRow label="Utility" value={utilities} />
            )}
            <MetadataRow
                label="Coordinates"
                value={`(${bus.latitude.toFixed(3)}, ${bus.longitude.toFixed(
                    3
                )})`}
            />
        </div>
    );
};

// Renders two sibling cells (no wrapper) so the parent metadata grid can
// align all labels and values into shared columns.
const MetadataRow: React.FC<{
    readonly label: string;
    readonly value: string;
}> = ({ label, value }) => {
    return (
        <>
            <div className="BusGeneralInfoSection-row-label bp5-text-disabled">
                {label}
            </div>
            <div className="BusGeneralInfoSection-row-value">{value}</div>
        </>
    );
};

const SatelliteThumbnail: React.FC<{
    readonly latitude: number;
    readonly longitude: number;
}> = ({ latitude, longitude }) => {
    const { mapboxToken } = getEnvVariables();
    const imageUrl = getSatelliteImageUrl(latitude, longitude, mapboxToken);
    const googleMapsUrl = getGoogleMapsUrl(latitude, longitude);

    return (
        <div
            className="BusGeneralInfoSection-satellite"
            onClick={() => window.open(googleMapsUrl, "_blank")}
        >
            <img
                src={imageUrl}
                alt="Satellite view"
                className="BusGeneralInfoSection-satellite-image"
            />
        </div>
    );
};

export default BusGeneralInfoSection;
